/* eslint-disable complexity */
import React, { useEffect, useRef, useContext } from 'react';
import './Voucher.css';
import PropTypes from 'prop-types';
import InfoIcon from '../../assets/InfoIcon.jsx';
import VodaPayClubIcon from '../../assets/VodaPayClubIcon.jsx';
import { SubscriptionDetailsContext } from '../../services/context/SubscriptionDetailsContext.js';
import { LimitsContext } from '../../services/context/LimitsContext.js';
import { getVoucherLimitInfo } from '../../services/utils/vendorLimits.js';
import { CartContext } from '../../services/context/CartContext.js';
import { MoneyFormatterContext } from '../../services/context/MoneyFormatterContext.js';
import { EligibilityContext } from '../../services/context/EligibilityContext.js';
import { createIsAdvanceOptionFilter } from '../../services/EligibilityOffers.js';

const VoucherHeader = ({
  iconUrl, title, addedValue, isClubVoucher,
  stock, remaining, voucherId, voucher,
}) => {
  const subscriptionDetails = useContext(SubscriptionDetailsContext);
  const limits = useContext(LimitsContext);
  const isSubscribed = subscriptionDetails?.isSubscribed;
  const applyCss = !isSubscribed && isClubVoucher;
  const logoRef = useRef(null);
  const cart = useContext(CartContext);
  const MoneyFormatter = useContext(MoneyFormatterContext);
  const eligibility = useContext(EligibilityContext);

  // Check voucher is advance voucher or not and pass the info to getVoucherLimitInfo
  const isAdvance = voucherObj => {
    const isAdvanceOption = createIsAdvanceOptionFilter(
      eligibility,
      voucherObj,
      MoneyFormatter,
    );
    return Boolean(voucherObj?.options?.advance.terms.options?.some(option => isAdvanceOption(option)));
  };

  const {
    stock: actualStock,
    used: actualUsed,
    isFullyReserved,
    hasLimitData: rawHasLimitData,
  } = getVoucherLimitInfo(limits, voucherId, cart, {
    fallbackStock: stock,
    fallbackRemaining: remaining,
  }, isAdvance(voucher));

  const remainingCount = actualStock - actualUsed;

  useEffect(() => {
    logoRef.current?.style.setProperty('--image-url', `url(${iconUrl})`);
  }, [logoRef.current]);

  let stockMessage = null;
  if (isFullyReserved) {
    stockMessage = `${actualStock} of ${actualStock} reserved`;
  } else if (remainingCount) {
    stockMessage = `${remainingCount} of ${actualStock} left`;
  }

  return (
    <header className='container'>
      {
        iconUrl
        && <div
          className='logo'
          ref={ logoRef }
        />
      }
      <section className={ applyCss ? 'info v-container v-disable' : 'info v-container' }>
        <h3>{title}</h3>
        <div className='added-value container-v'>
          <p>{addedValue}</p>
          {
            isClubVoucher
            && <VodaPayClubIcon />
          }
          {
            (!iconUrl || isAdvance(voucher))
            && <InfoIcon />
          }
        </div>
        {
          rawHasLimitData && isSubscribed && isClubVoucher && (
            <div
              className={ `stock-rate-limit ${isFullyReserved ? 'reserved' : ''}` }
            >
              { stockMessage }
            </div>
          )
        }
      </section>
    </header>
  );
};

VoucherHeader.propTypes = {
  iconUrl: PropTypes.string,
  title: PropTypes.element,
  addedValue: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
  ]),
  isClubVoucher: PropTypes.bool,
  stock: PropTypes.number,
  remaining: PropTypes.number,
  voucherId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  voucher: PropTypes.object,
};

/**
 * @param {object} props - The props for the component
 * @param {string} props.iconUrl - The URL for the logo
 * @param {JSX.Element} props.title - The Title element
 * @param {JSX.Element | string} props.addedValue - Element for added value
 * @param {JSX.Element} props.actionItem - Action item element
 * @param {Function} props.onClick - Function for clicking on component
 * @param {JSX.Element} props.isClubVoucher - Prop to check ClubVoucher category
 * @param props.stock
 * @param props.remaining
 * @param {JSX.Element} props.voucherId
 * @param props.disableLimits
 * @param {JSX.Element} props.voucher - voucher object
 * @returns {JSX.Element} Voucher Component
 */
export default function Voucher({
  iconUrl, title, addedValue, actionItem, onClick, isClubVoucher,
  stock, remaining, voucherId, disableLimits, voucher,
}) {
  const empty = (iconUrl ?? title ?? addedValue ?? actionItem ?? onClick) === undefined;

  return (
    <div className={ `voucher container-v${empty ? ' empty skeleton-loader' : ''}` }>
      {
        onClick ? <button
          onClick={ onClick }
          className='on-click'
        >
          <VoucherHeader
            iconUrl={ iconUrl }
            title={ title }
            addedValue={ addedValue }
            isClubVoucher={ isClubVoucher }
            stock={ stock }
            remaining={ remaining }
            voucherId={ voucherId }
            disableLimits={ disableLimits }
            voucher={ voucher }
          />
        </button>
          : <div className='on-click'>
            <VoucherHeader
              iconUrl={ iconUrl }
              title={ title }
              addedValue={ addedValue }
              isClubVoucher={ isClubVoucher }
              stock={ stock }
              remaining={ remaining }
              voucherId={ voucherId }
              disableLimits={ disableLimits }
              voucher={ voucher }
            />
          </div>
      }
      {
        actionItem
        && <div className='action-item'>{actionItem}</div>
      }
      {
        empty
        && <div className='action-item'>
          <div />
        </div>
      }
    </div>
  );
}

Voucher.propTypes = {
  onClick: PropTypes.func,
  iconUrl: PropTypes.string,
  title: PropTypes.element,
  addedValue: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
  ]),
  actionItem: PropTypes.element,
  isClubVoucher: PropTypes.bool,
  stock: PropTypes.number,
  remaining: PropTypes.number,
  voucherId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  disableLimits: PropTypes.bool,
  voucher: PropTypes.object,
};
